import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ChartType } from 'chart.js';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { PriceDistributionService } from 'src/app/common/services/price-distribution.service';

@Component({
  selector: 'app-chart-cell-renderer',
  templateUrl: './chart-cell-renderer.component.html',
  styleUrls: ['./chart-cell-renderer.component.sass'],
  providers: [DialogService]
})
export class ChartCellRendererComponent implements ICellRendererAngularComp, OnInit, OnDestroy {
  public params: any;
  public rawChartData: any = null;
  public chartData: any = {};
  public chartType: ChartType = 'bar';
  public chartOptions: any = {};
  public chartLabels: string[] = [];
  public chartDatasets: any[] = [];
  public showChart: boolean = false;
  public isMixedChart: boolean = false;
  constructor(private dialogService: DialogService) {}

  agInit(params: any): void {
    this.params = params;
    this.extractChartData();
    this.setupChart();
  }

  ngOnInit(): void {
    // Component initialization
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  refresh(params: any): boolean {
    this.params = params;
    this.extractChartData();
    this.setupChart();
    return true;
  }

  private extractChartData(): void {
    if (!this.params?.value) {
      this.rawChartData = null;
      this.showChart = false;
      return;
    }

    // Get chart configuration from cell renderer params
    const chartParams = this.params?.chartParams || {};
    this.chartType = (chartParams.type === 'histogram') ? 'bar' : chartParams.type || 'bar';

    // Extract data based on chart type and data structure
    if (chartParams.dataField) {
      // Use specific field from the data
      this.rawChartData = this.params.value[chartParams.dataField];
    } else if (chartParams.type === 'histogram' || chartParams.type === 'bar') {
      // Default to histogram data if available
      this.rawChartData = this.params.value.histogram || this.params.value;
    } else {
      // Use raw data
      this.rawChartData = this.params.value;
    }

    this.showChart = this.rawChartData && Array.isArray(this.rawChartData) && this.rawChartData.length > 0;
  }

  private setupChart(): void {
    if (!this.showChart || !this.rawChartData) {
      return;
    }

    // Setup labels and datasets based on chart type
    if (this.chartType === 'bar') {
      this.setupBarChart();
    } else if (this.chartType === 'line') {
      this.setupLineChart();
    } else if (this.chartType === 'doughnut' || this.chartType === 'pie') {
      this.setupPieChart();
    } else {
      this.setupBarChart(); // Default to bar chart
    }

    // Prepare chart data in ng2-charts format
    this.chartData = {
      labels: this.chartLabels,
      datasets: this.chartDatasets
    };

    // Setup chart options (Chart.js 3.x syntax)
    const colors = this.getChartColors();
    
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            afterLabel: (context: any) => {
              const datasetIndex = context.datasetIndex;
              const dataIndex = context.dataIndex;
              
              // Only show for the bar chart (not the distribution line)
              if (datasetIndex === 0 && context.dataset.metadata && context.dataset.metadata[dataIndex]) {
                const metadata = context.dataset.metadata[dataIndex];
                const temporalData = metadata.temporalData;
                
                const lines: string[] = [];
                
                if (temporalData) {
                  lines.push('');
                  lines.push('Date Range:');
                  if (temporalData.earliestDate) {
                    lines.push(`  From: ${new Date(temporalData.earliestDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`);
                  }
                  if (temporalData.latestDate) {
                    lines.push(`  To: ${new Date(temporalData.latestDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`);
                  }
                  
                  if (temporalData.monthlyBreakdown && temporalData.monthlyBreakdown.length > 0) {
                    lines.push('');
                    lines.push('Items by Month:');
                    temporalData.monthlyBreakdown.forEach((monthData: any) => {
                      lines.push(`  ${monthData.monthName}: ${monthData.count}`);
                    });
                  }
                }
                
                return lines;
              }
              
              return [];
            }
          }
        }
      },
      scales: this.chartType === 'doughnut' || this.chartType === 'pie' ? undefined : {
        y: {
          beginAtZero: true,
          grid: {
            color: colors.gridColor
          },
          ticks: {
            color: colors.textColor,
            font: {
              size: 10
            }
          }
        },
        x: {
          grid: {
            color: colors.gridColor
          },
          ticks: {
            color: colors.textColor,
            font: {
              size: 9
            },
            maxRotation: 45,
            minRotation: 0
          }
        }
      }
    };
  }

  private setupBarChart(): void {
    const colors = this.getChartColors();
    
    // Check if we have the full distribution data or just histogram
    const fullData = this.params.value;
    const histogram = fullData.histogram || this.rawChartData;
    
    this.chartLabels = histogram.map((bin: any) => 
      `${bin.binStart}-${bin.binEnd}`
    );
    
    const datasets: any[] = [{
      label: 'Frequency',
      data: histogram.map((bin: any) => bin.count),
      backgroundColor: colors.backgroundColor,
      borderColor: colors.borderColor,
      borderWidth: 1,
      type: 'bar',
      // Store metadata for tooltip
      metadata: histogram.map((bin: any) => ({
        temporalData: bin.temporalData,
        items: bin.items
      }))
    }];
    
    // Add distribution curve if we have mean and stdDev
    if (fullData.mean !== undefined && fullData.stdDev !== undefined) {
      const curveData = this.generateDistributionCurve(histogram, fullData.mean, fullData.stdDev, fullData.count);
      if (curveData) {
        // Get yellow status color
        const computedStyle = getComputedStyle(document.documentElement);
        const yellowColor = computedStyle.getPropertyValue('--yellow-status').trim() || '#FFC107';
        
        datasets.push({
          label: 'Distribution',
          data: curveData,
          borderColor: yellowColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          type: 'line',
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0
        });
      }
    }
    
    this.chartDatasets = datasets;
  }

  private setupLineChart(): void {
    const colors = this.getChartColors();
    
    this.chartLabels = this.rawChartData.map((item: any, index: number) => 
      item.label || item.binStart || index.toString()
    );
    
    this.chartDatasets = [{
      label: 'Value',
      data: this.rawChartData.map((item: any) => 
        item.value || item.count || item.percentage
      ),
      borderColor: colors.borderColor,
      backgroundColor: colors.backgroundColor,
      fill: false
    }];
  }

  private setupPieChart(): void {
    this.chartLabels = this.rawChartData.map((item: any) => 
      item.label || `${item.binStart}-${item.binEnd}`
    );
    
    this.chartDatasets = [{
      data: this.rawChartData.map((item: any) => 
        item.percentage || item.count
      ),
      backgroundColor: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
      ]
    }];
  }

  private getChartColors(): any {
    // Get the primary color from CSS variable
    const computedStyle = getComputedStyle(document.documentElement);
    const primaryColor = computedStyle.getPropertyValue('--primary-color').trim();
    
    // Convert hex to rgba for transparency
    const primaryColorRgb = this.hexToRgb(primaryColor);
    
    return {
      backgroundColor: primaryColorRgb ? `rgba(${primaryColorRgb.r}, ${primaryColorRgb.g}, ${primaryColorRgb.b}, 0.6)` : 'rgba(34, 197, 94, 0.6)',  // Green with transparency
      borderColor: primaryColorRgb ? `rgba(${primaryColorRgb.r}, ${primaryColorRgb.g}, ${primaryColorRgb.b}, 1)` : 'rgba(34, 197, 94, 1)',
      gridColor: 'rgba(128, 128, 128, 0.2)',
      textColor: 'rgba(128, 128, 128, 1)'
    };
  }

  private hexToRgb(hex: string): {r: number, g: number, b: number} | null {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse the hex value
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  private generateDistributionCurve(histogram: any[], mean: number, stdDev: number, totalCount: number): number[] | null {
    if (!histogram || histogram.length === 0 || stdDev === 0) {
      return null;
    }
    
    // Calculate the normal distribution curve points
    const curvePoints: number[] = [];
    
    for (const bin of histogram) {
      const binCenter = (bin.binStart + bin.binEnd) / 2;
      
      // Normal distribution probability density function
      const exponent = -0.5 * Math.pow((binCenter - mean) / stdDev, 2);
      const coefficient = 1 / (stdDev * Math.sqrt(2 * Math.PI));
      const density = coefficient * Math.exp(exponent);
      
      // Scale to match histogram counts
      const binWidth = bin.binEnd - bin.binStart;
      const expectedCount = density * binWidth * totalCount;
      
      curvePoints.push(Number(expectedCount.toFixed(2)));
    }
    
    return curvePoints;
  }

  expandChart(): void {
    if (!this.showChart || !this.rawChartData) {
      return;
    }

    const colors = this.getChartColors();

    // Determine entity type and cost type from the params
    const entityId = this.params?.data?.id;
    const entityType = this.determineEntityType();
    const costType = this.determineCostType();

    // Create a detailed chart modal
    this.dialogService.open(ChartDetailDialogComponent, {
      header: 'Cost Distribution Details',
      width: '900px',
      height: 'auto',
      data: {
        chartType: this.isMixedChart ? 'line' : this.chartType,
        chartLabels: this.chartLabels,
        chartDatasets: this.chartDatasets,
        chartOptions: {
          ...this.chartOptions,
          maintainAspectRatio: true,
          plugins: {
            ...this.chartOptions.plugins,
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: colors.textColor
              }
            },
            title: {
              display: true,
              text: `${this.chartType === 'bar' ? 'Cost Distribution' : 'Data Visualization'}`,
              color: colors.textColor
            }
          }
        },
        rawData: this.rawChartData,
        categoryName: this.params?.data?.name || 'Category',
        fullData: this.params.value,
        isMixedChart: this.isMixedChart,
        // New properties for filtering
        entityId: entityId,
        entityType: entityType,
        costType: costType
      }
    });
  }

  private determineEntityType(): string {
    // Check if we're in categories or tags table
    const colId = this.params?.column?.colId || '';
    if (colId.includes('categoryStats') || this.params?.context?.entity === 'categories') {
      return 'categories';
    } else if (colId.includes('tagStats') || this.params?.context?.entity === 'tags') {
      return 'tags';
    }
    return 'categories'; // default
  }

  private determineCostType(): 'coin' | 'diamond' {
    // Check the column ID or header name to determine cost type
    const colId = this.params?.column?.colId || '';
    const headerName = this.params?.colDef?.headerName || '';
    
    if (colId.includes('coin') || headerName.toLowerCase().includes('coin')) {
      return 'coin';
    } else if (colId.includes('diamond') || headerName.toLowerCase().includes('diamond')) {
      return 'diamond';
    }
    return 'coin'; // default
  }
}

// Chart Detail Dialog Component
@Component({
  selector: 'app-chart-detail-dialog',
  templateUrl: './chart-detail-dialog.component.html',
  styleUrls: ['./chart-detail-dialog.component.sass']
})
export class ChartDetailDialogComponent implements OnInit {
  public chartData: any = {};
  public startDate: Date | null = null;
  public endDate: Date | null = null;
  public isLoading: boolean = false;
  public filtersApplied: boolean = false;
  public appliedDateRange: string = '';
  public totalItemsBeforeFilter: number = 0;
  public totalItemsAfterFilter: number = 0;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private priceDistributionService: PriceDistributionService
  ) {}

  ngOnInit(): void {
    // Prepare chart data in the format expected by ng2-charts
    this.chartData = {
      labels: this.config.data.chartLabels,
      datasets: this.config.data.chartDatasets
    };
    
    // Save initial count
    this.totalItemsBeforeFilter = this.config.data.fullData?.count || 0;
    this.totalItemsAfterFilter = this.totalItemsBeforeFilter;
  }

  async applyFilters(): Promise<void> {
    if (!this.config.data.entityId || !this.config.data.entityType || !this.config.data.costType) {
      console.error('Missing required data for filtering');
      return;
    }

    this.isLoading = true;
    
    try {
      const filters: any = {
        costType: this.config.data.costType
      };

      if (this.startDate) {
        filters.startDate = this.startDate.toISOString().split('T')[0];
      }
      if (this.endDate) {
        filters.endDate = this.endDate.toISOString().split('T')[0];
      }

      // Call the backend API using the service
      this.priceDistributionService.getFilteredDistribution(
        this.config.data.entityType,
        this.config.data.entityId,
        filters
      ).subscribe({
        next: (distributionData) => {
          // Update the chart with new data
          this.updateChartWithNewData(distributionData);
          
          // Mark filters as applied
          this.filtersApplied = true;
          this.totalItemsAfterFilter = distributionData.count || 0;
          
          // Build date range string
          const startStr = this.startDate ? this.startDate.toISOString().split('T')[0] : 'Beginning';
          const endStr = this.endDate ? this.endDate.toISOString().split('T')[0] : 'Now';
          this.appliedDateRange = `${startStr} to ${endStr}`;
        },
        error: (error) => {
          console.error('Error applying filters:', error);
          alert('Failed to apply filters. Please try again.');
        },
        complete: () => {
          this.isLoading = false;
        }
      });
      
    } catch (error) {
      console.error('Error applying filters:', error);
      alert('Failed to apply filters. Please try again.');
      this.isLoading = false;
    }
  }

  clearFilters(): void {
    this.startDate = null;
    this.endDate = null;
    this.filtersApplied = false;
    this.appliedDateRange = '';
    
    // Restore original chart data
    this.chartData = {
      labels: this.config.data.chartLabels,
      datasets: this.config.data.chartDatasets
    };
    
    // Restore original stats
    this.config.data.rawData = this.config.data.originalRawData || this.config.data.rawData;
    this.totalItemsAfterFilter = this.totalItemsBeforeFilter;
    this.config.data.fullData = this.config.data.originalFullData || this.config.data.fullData;
    
    // Reset counts
    this.totalItemsAfterFilter = this.totalItemsBeforeFilter;
  }

  private updateChartWithNewData(distributionData: any): void {
    // Save original data if not already saved
    if (!this.config.data.originalRawData) {
      this.config.data.originalRawData = [...this.config.data.rawData];
      this.config.data.originalFullData = { ...this.config.data.fullData };
    }

    // Update rawData and fullData
    this.config.data.rawData = distributionData.histogram || [];
    this.config.data.fullData = distributionData;

    // Regenerate chart labels and datasets
    const histogram = distributionData.histogram || [];
    const newLabels = histogram.map((bin: any) => `${bin.binStart}-${bin.binEnd}`);
    
    const colors = this.getChartColors();
    const newDatasets: any[] = [{
      label: 'Frequency',
      data: histogram.map((bin: any) => bin.count),
      backgroundColor: colors.backgroundColor,
      borderColor: colors.borderColor,
      borderWidth: 1,
      type: 'bar',
      // Store metadata for tooltip
      metadata: histogram.map((bin: any) => ({
        temporalData: bin.temporalData,
        items: bin.items
      }))
    }];

    // Add distribution curve if we have mean and stdDev
    if (distributionData.mean !== undefined && distributionData.stdDev !== undefined) {
      const curveData = this.generateDistributionCurve(
        histogram, 
        distributionData.mean, 
        distributionData.stdDev, 
        distributionData.count
      );
      
      if (curveData) {
        const computedStyle = getComputedStyle(document.documentElement);
        const yellowColor = computedStyle.getPropertyValue('--yellow-status').trim() || '#FFC107';
        
        newDatasets.push({
          label: 'Distribution',
          data: curveData,
          borderColor: yellowColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          type: 'line',
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0
        });
      }
    }

    // Update chart data
    this.chartData = {
      labels: newLabels,
      datasets: newDatasets
    };

    // Update chart options to include date range in tooltip
    if (!this.config.data.chartOptions.plugins) {
      this.config.data.chartOptions.plugins = {};
    }
    if (!this.config.data.chartOptions.plugins.tooltip) {
      this.config.data.chartOptions.plugins.tooltip = {};
    }
    
    // Store original tooltip callback
    const originalCallback = this.config.data.chartOptions.plugins.tooltip.callbacks?.afterLabel;
    
    this.config.data.chartOptions.plugins.tooltip.callbacks = {
      ...this.config.data.chartOptions.plugins.tooltip.callbacks,
      afterLabel: (context: any) => {
        const datasetIndex = context.datasetIndex;
        const dataIndex = context.dataIndex;
        
        // Only show for the bar chart (not the distribution line)
        if (datasetIndex === 0 && context.dataset.metadata && context.dataset.metadata[dataIndex]) {
          const metadata = context.dataset.metadata[dataIndex];
          const temporalData = metadata.temporalData;
          
          const lines: string[] = [];
          
          if (temporalData) {
            lines.push('');
            lines.push('Date Range:');
            if (temporalData.earliestDate) {
              lines.push(`  From: ${new Date(temporalData.earliestDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`);
            }
            if (temporalData.latestDate) {
              lines.push(`  To: ${new Date(temporalData.latestDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`);
            }
            
            if (temporalData.monthlyBreakdown && temporalData.monthlyBreakdown.length > 0) {
              lines.push('');
              lines.push('Items by Month:');
              temporalData.monthlyBreakdown.forEach((monthData: any) => {
                lines.push(`  ${monthData.monthName}: ${monthData.count}`);
              });
            }
          }
          
          return lines;
        }
        
        // Call original callback if exists
        return originalCallback ? originalCallback(context) : [];
      }
    };
  }

  private generateDistributionCurve(histogram: any[], mean: number, stdDev: number, totalCount: number): number[] | null {
    if (!histogram || histogram.length === 0 || stdDev === 0) {
      return null;
    }
    
    const curvePoints: number[] = [];
    
    for (const bin of histogram) {
      const binCenter = (bin.binStart + bin.binEnd) / 2;
      const exponent = -0.5 * Math.pow((binCenter - mean) / stdDev, 2);
      const coefficient = 1 / (stdDev * Math.sqrt(2 * Math.PI));
      const density = coefficient * Math.exp(exponent);
      const binWidth = bin.binEnd - bin.binStart;
      const expectedCount = density * binWidth * totalCount;
      
      curvePoints.push(Number(expectedCount.toFixed(2)));
    }
    
    return curvePoints;
  }

  private getChartColors(): any {
    const computedStyle = getComputedStyle(document.documentElement);
    const primaryColor = computedStyle.getPropertyValue('--primary-color').trim();
    const primaryColorRgb = this.hexToRgb(primaryColor);
    
    return {
      backgroundColor: primaryColorRgb ? `rgba(${primaryColorRgb.r}, ${primaryColorRgb.g}, ${primaryColorRgb.b}, 0.6)` : 'rgba(34, 197, 94, 0.6)',
      borderColor: primaryColorRgb ? `rgba(${primaryColorRgb.r}, ${primaryColorRgb.g}, ${primaryColorRgb.b}, 1)` : 'rgba(34, 197, 94, 1)',
    };
  }

  private hexToRgb(hex: string): {r: number, g: number, b: number} | null {
    hex = hex.replace('#', '');
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  getTotalCount(): number {
    return this.config.data.rawData.reduce((total: number, item: any) => total + (item.count || 0), 0);
  }

  getRange(): string {
    const min = Math.min(...this.config.data.rawData.map((item: any) => item.binStart));
    const max = Math.max(...this.config.data.rawData.map((item: any) => item.binEnd));
    return `${min} - ${max}`;
  }
}